import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AdminSubpage from '../components/AdminSubpage';
import { permissionsAPI, usersAPI } from '../services/api';

const PermissionManagePage = () => {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [rolesRes, permissionsRes] = await Promise.all([
          permissionsAPI.getRoles(),
          permissionsAPI.getPermissions(),
        ]);
        if (!cancelled) {
          setRoles(rolesRes.data || []);
          setPermissions(permissionsRes.data || []);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            'Error loading permissions data: ' + (error.response?.data?.message || error.message)
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await usersAPI.getAll({ per_page: 100 });
        if (!cancelled) setUsers(response.data.data || []);
      } catch (error) {
        if (!cancelled) console.error('Error loading users:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadUserPermissions = async (userId) => {
    try {
      const response = await permissionsAPI.getUserRoles(userId);
      setUserRoles(response.data.roles || []);
      setUserPermissions(response.data.permissions || []);
    } catch (error) {
      toast.error(
        'Error loading user permissions: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleSelectUser = async (userId) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setSelectedUser(user);
      await loadUserPermissions(userId);
    }
  };

  const handleAssignRole = async (roleId) => {
    if (!selectedUser) return;
    try {
      await permissionsAPI.assignRole(selectedUser.id, roleId);
      await loadUserPermissions(selectedUser.id);
      toast.success('Role assigned successfully!');
    } catch (error) {
      toast.error('Error assigning role: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRemoveRole = async (roleId) => {
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to remove this role?')) return;
    try {
      await permissionsAPI.removeRole(selectedUser.id, roleId);
      await loadUserPermissions(selectedUser.id);
      toast.success('Role removed successfully!');
    } catch (error) {
      toast.error('Error removing role: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleAssignPermission = async (permissionId) => {
    if (!selectedUser) return;
    try {
      await permissionsAPI.assignPermission(selectedUser.id, permissionId);
      await loadUserPermissions(selectedUser.id);
      toast.success('Permission assigned successfully!');
    } catch (error) {
      toast.error(
        'Error assigning permission: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleRemovePermission = async (permissionId) => {
    if (!selectedUser) return;
    if (!window.confirm('Are you sure you want to remove this permission?')) return;
    try {
      await permissionsAPI.removePermission(selectedUser.id, permissionId);
      await loadUserPermissions(selectedUser.id);
      toast.success('Permission removed successfully!');
    } catch (error) {
      toast.error(
        'Error removing permission: ' + (error.response?.data?.message || error.message)
      );
    }
  };

  if (loading) {
    return (
      <AdminSubpage title="Manage user permissions" backTo="/permissions">
        <div className="loading">Loading permissions...</div>
      </AdminSubpage>
    );
  }

  const filteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      (user.first_name && user.first_name.toLowerCase().includes(search.toLowerCase())) ||
      (user.last_name && user.last_name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <AdminSubpage title="Manage user permissions" backTo="/permissions">
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '100%', padding: '10px', marginBottom: '10px', maxWidth: 480 }}
        />
        <div
          style={{
            maxHeight: '200px',
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
          }}
        >
          {filteredUsers.map((user) => (
            <div
              key={user.id}
              role="button"
              tabIndex={0}
              onClick={() => handleSelectUser(user.id)}
              onKeyPress={(e) => e.key === 'Enter' && handleSelectUser(user.id)}
              style={{
                padding: '10px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                backgroundColor: selectedUser?.id === user.id ? '#e8ecff' : 'white',
              }}
            >
              {user.email} - {user.first_name} {user.last_name}
            </div>
          ))}
        </div>
      </div>

      {selectedUser ? (
        <div className="admin-page-panel modal-content large">
          <h3>Managing: {selectedUser.email}</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '20px' }}>
            <div>
              <h4>Current Roles</h4>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                }}
              >
                {userRoles.length === 0 ? (
                  <p style={{ color: '#999' }}>No roles assigned</p>
                ) : (
                  userRoles.map((role) => (
                    <div
                      key={role.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 0',
                      }}
                    >
                      <span>{role.name}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemoveRole(role.id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
              <h4 style={{ marginTop: '20px' }}>Assign Role</h4>
              <select
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleAssignRole(e.target.value);
                    e.target.value = '';
                  }
                }}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Select a role...</option>
                {roles
                  .filter((role) => !userRoles.find((ur) => ur.id === role.id))
                  .map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <h4>Current Permissions</h4>
              <div
                style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                }}
              >
                {userPermissions.length === 0 ? (
                  <p style={{ color: '#999' }}>No direct permissions assigned</p>
                ) : (
                  userPermissions.map((permission) => (
                    <div
                      key={permission.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 0',
                      }}
                    >
                      <span>{permission.name}</span>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleRemovePermission(permission.id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
              <h4 style={{ marginTop: '20px' }}>Assign Permission</h4>
              <select
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleAssignPermission(e.target.value);
                    e.target.value = '';
                  }
                }}
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Select a permission...</option>
                {permissions
                  .filter((perm) => !userPermissions.find((up) => up.id === perm.id))
                  .map((permission) => (
                    <option key={permission.id} value={permission.id}>
                      {permission.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      ) : (
        <p style={{ color: '#666' }}>Select a user above to assign roles and permissions.</p>
      )}
    </AdminSubpage>
  );
};

export default PermissionManagePage;
